import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { Loader } from '@components/atoms/loader';
import { ToastContainer } from '@components/moleculs/toast-container';
import deviceDetect from '@dataspike/device-detect';
import { useScreenFullHeight } from '@hooks/use-screen-full-height';
import type { ConsentShownInEnum , VerificationProfileModel, VerificationProfileSettingsModel } from '@models';
import type { DocsVerificationPhotosKeysEnum } from '@models/enums';
import { DocsVerificationStepEnum, LangEnum } from '@models/enums';
import { DocsVerificationSteps } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/docs-verification-steps';
import { VerificationError } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/docs-verification-steps/verification-error';
import { DemoDataContextProvider } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/demo-data-provider';
import { FaceDetectorContextProvider } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/face-detector-provider';
import { WidgetContextProvider } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/providers/widget-provider';
import { PersonalDataContextProvider } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data/provider';
import { VERIFICATION_RESPONSE } from '@modules/docs-verification-widget/demo-responses/verification-response';
import { useDeviceOrientation } from '@modules/docs-verification-widget/hooks/use-device-orientation';
import type { GetVerificationResponse } from '@modules/docs-verification-widget/models/get-verification-response';
import type { DomainSettingsModel } from '@modules/shared/models/white-label/domain-settings-model';
import { domainSlice } from '@modules/shared/slices/domain-slice';
import Box from '@mui/material/Box';
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline';
import type { ThemeOptions } from '@mui/material/styles';
import {
    createTheme,
    StyledEngineProvider,
    ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { setupListeners } from '@reduxjs/toolkit/query/react';
import type { StoreWrapperProps } from '@root/types';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { themeSlice } from '@store/slices/theme-slice';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import type { DefaultTheme } from 'styled-components';
import { ThemeProvider } from 'styled-components';

import { generateMediaHelpers, theme } from '../../theme';

import {
    sdkApi,
    useLazyGetCustomPartnerVerificationByIdQuery,
    useLazyGetVerificationByIdQuery,
    useUploadFingerprintSDKMutation,
} from './api';
import { GlobalStyles } from './global-styles';
import { createLocalization, MUI_LOCALES } from './localization';
import { store, useAppDispatch, useAppSelector } from './store';
import { DocsVerificationWidgetContainer } from './styles';
import type { DocsVerificationWidgetInstance, DocsVerificationWidgetProps } from './types';

setupListeners(store.dispatch);
const i18nInstance = createLocalization();

const convertChecks = (settings: VerificationProfileModel['settings']) =>
    [
        ...(settings.poi_required ? settings.poi_allowed_documents : []),
        ...(settings.face_comparison_required ? settings.face_comparison_allowed_documents : []),
        ...(settings.poa_required ? ['poa'] : []),
    ] as DocsVerificationPhotosKeysEnum[];

const StoreWrapper = ({ children }: StoreWrapperProps) => (
    <Provider store={store}>{children}</Provider>
);

export const Widget = ({
    onInit,
    settings,
    theme: customTheme,
    isDemo,
    defaultLanguage,
    logo,
    className,
    style,
    ...rest
}: DocsVerificationWidgetProps) => {
    const { i18n, ready } = useTranslation();
    const externalSettings = settings as DomainSettingsModel;
    const domainSettings = useAppSelector((state) => state.domain.settings);
    const currentTheme = useAppSelector((state) => state.theme.currentTheme);
    const sdkOptions = useAppSelector((state) => state.docsVerification.sdkOptions);
    const currentStep = useAppSelector((state) => state.docsVerification.currentStep);
    const dispatch = useAppDispatch();
    const { isLandscape } = useDeviceOrientation();
    const fingerprintIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const [uploadFingerprint] = useUploadFingerprintSDKMutation();
    const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const lang = searchParams.get('lang')?.toLowerCase();

        let actualLang = '';

        Object.values(LangEnum).forEach((val) => {
            if (val === lang) {
                actualLang = val;
            }
        });

        i18nInstance.changeLanguage(actualLang || defaultLanguage || LangEnum.EN);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const mainTheme = useMemo(
        () =>
            createTheme(
                merge(cloneDeep({ ...(currentTheme as ThemeOptions) }), domainSettings.ui_settings),
                merge(
                    { ...theme },
                    { ...MUI_LOCALES[i18n.language as keyof typeof MUI_LOCALES] },
                    customTheme,
                ),
                generateMediaHelpers(currentTheme),
            ),
        [currentTheme, domainSettings, customTheme, i18n.language],
    ) as unknown as DefaultTheme;

    const [getVerificationById] = useLazyGetVerificationByIdQuery();
    const [getCustomVerificationById] = useLazyGetCustomPartnerVerificationByIdQuery();
    const isInitialized = useRef(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const { height } = useScreenFullHeight();

    useEffect(() => {
        dispatch(themeSlice.actions.setIsLandscape(isLandscape));
    }, [dispatch, isLandscape]);

    useEffect(() => {
        if (externalSettings || logo) {
            dispatch(
                domainSlice.actions.setSettings(
                    merge(cloneDeep(domainSettings), externalSettings, {
                        ...(logo
                            ? {
                                  logo_settings: {
                                      logo_wide_url: logo,
                                  },
                              }
                            : {}),
                    }),
                ),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;

            const init = async () => {
                try {
                    let verification: GetVerificationResponse;

                    const params = {
                        id: rest.id,
                        url: rest.apiUrl,
                    };

                    if (isDemo) {
                        verification = VERIFICATION_RESPONSE;
                    } else {
                        verification = await getVerificationById(params).unwrap();
                    }

                    const isSkipIntro = searchParams.get('skipIntro') === 'true';
                    const isConcodrium = rest.id?.toLowerCase().startsWith('ccd');

                    let redirectUrl = '';

                    if (isConcodrium) {
                        try {
                            const response = await getCustomVerificationById(params).unwrap();

                            if (!response?.redirect_uri) {
                                await onInit?.('error', 'redirect_uri is empty');
                                setIsError(true);

                                return;
                            }
                            redirectUrl = response.redirect_uri;
                        } catch (e) {
                            await onInit?.('error', e);
                            setIsError(true);

                            return;
                        }
                    }

                    if (onInit) {
                        await onInit(verification.status);
                    }

                    const search = new URLSearchParams(window.location.search);
                    const source = search.get('source');

                    dispatch(
                        docsVerificationSlice.actions.useSDK({
                            id: rest.id,
                            elementId: rest.elementId,
                            settings: verification.settings as VerificationProfileSettingsModel,
                            checks: verification.checks
                                ? convertChecks(
                                      verification.settings as VerificationProfileSettingsModel,
                                  )
                                : [],
                            apiUrl: rest.apiUrl,
                            logsUrl: rest.logsUrl,
                            redirectUrl: redirectUrl || rest.redirectUrl,
                            status: verification.status,
                            verificationUrl: verification.verification_url,
                            expiresAt: verification.expires_at,
                            source,
                            isDemo: !!isDemo,
                            isWeb3: verification.settings?.is_web3,
                            ...(verification.settings?.is_web3
                                ? {
                                      web3Options: {
                                          wallet: verification.wallet as string,
                                      },
                                  }
                                : {}),
                            customPartner: isConcodrium
                                ? {
                                      concordium: true,
                                  }
                                : {},
                            isSkipIntro,
                            consentShownIn: rest.consentShownIn as ConsentShownInEnum | undefined,
                        }),
                    );
                } catch (e) {
                    if (onInit) {
                        await onInit('error', e);
                    }
                    setIsError(true);
                } finally {
                    setIsLoading(false);
                }
            };

            init();
        }
    }, [
        getVerificationById,
        getCustomVerificationById,
        rest,
        dispatch,
        onInit,
        isDemo,
        searchParams,
    ]);

    useEffect(() => {
        if (sdkOptions?.id && currentStep === DocsVerificationStepEnum.VERIFICATION) {
            const checkDevice = () => {
                deviceDetect
                    .init({
                        checkCamera: true,
                    })
                    .then((response) => {
                        response.get().then((deviceData) => {
                            uploadFingerprint({
                                url: sdkOptions.apiUrl,
                                shortVerificationId: sdkOptions.id,
                                identifier: deviceData.id,
                                ...deviceData.checks,
                            });
                        });
                    });
            };

            checkDevice();

            fingerprintIntervalRef.current = setInterval(() => {
                checkDevice();
            }, 15000);
        }

        return () => {
            if (fingerprintIntervalRef.current) {
                clearInterval(fingerprintIntervalRef.current);
            }
        };
    }, [uploadFingerprint, sdkOptions, currentStep]);

    const isWidgetLoading = useMemo(() => isLoading || !ready, [isLoading, ready]);

    return (
        <I18nextProvider i18n={i18nInstance}>
            <StyledEngineProvider injectFirst={true}>
                <MuiThemeProvider theme={mainTheme}>
                    <ThemeProvider theme={mainTheme}>
                        <DemoDataContextProvider>
                            <PersonalDataContextProvider>
                                <WidgetContextProvider containerRef={containerRef}>
                                    <DocsVerificationWidgetContainer
                                        id='documents-widget'
                                        ref={containerRef}
                                        className={className}
                                        style={style}
                                    >
                                        <FaceDetectorContextProvider>
                                            <ScopedCssBaseline>
                                                <GlobalStyles />
                                                <ToastContainer />
                                                <div>
                                                    {isWidgetLoading && (
                                                        <Box
                                                            style={{ height }}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Loader />
                                                        </Box>
                                                    )}
                                                    {isError && <VerificationError />}
                                                    {!isWidgetLoading && sdkOptions && (
                                                        <DocsVerificationSteps
                                                            onFinish={rest.onFinish}
                                                            getZkProof={rest.getZkProof}
                                                        />
                                                    )}
                                                </div>
                                            </ScopedCssBaseline>
                                        </FaceDetectorContextProvider>
                                    </DocsVerificationWidgetContainer>
                                </WidgetContextProvider>
                            </PersonalDataContextProvider>
                        </DemoDataContextProvider>
                    </ThemeProvider>
                </MuiThemeProvider>
            </StyledEngineProvider>
        </I18nextProvider>
    );
};

const resetWidgetStore = () => {
    store.dispatch(docsVerificationSlice.actions.clearSteps());
    store.dispatch(domainSlice.actions.clearSettings());
    store.dispatch(sdkApi.util.resetApiState());
};

export const DocsVerificationWidget = (
    props: DocsVerificationWidgetProps,
): DocsVerificationWidgetInstance => {
    const container = document.getElementById(props.elementId);
    let root: Root | null = null;
    let currentClassName = props.className;
    let currentStyle = props.style;

    const renderWidget = () => {
        if (!root) {
            return;
        }

        root.render(
            <React.StrictMode>
                <StoreWrapper>
                    <Widget {...props} className={currentClassName} style={currentStyle} />
                </StoreWrapper>
            </React.StrictMode>,
        );
    };

    if (container) {
        root = createRoot(container);
        renderWidget();
    }

    const destroy = () => {
        root?.unmount();
        resetWidgetStore();
    };

    const updateStyles = ({
        className,
        style,
    }: Pick<DocsVerificationWidgetProps, 'className' | 'style'>) => {
        if (className !== undefined) {
            currentClassName = className;
        }

        if (style !== undefined) {
            currentStyle = style;
        }

        renderWidget();
    };

    return { root, destroy, updateStyles };
};
